import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as appStorage from '../utils/appIndexedDB';
import { MAIL_NOTIFY_GUIDE_PAGES } from './mailNotifyGuideData';
import './MailNotifyGuide.css';

/**
 * 「信件即時通知」功能引導（AGUI-1113）：第一次進頁時跳出的多頁說明，之後可從標題旁的「使用說明」再打開。
 *
 * 使用者裁決（10-08）：
 * - 「看過了」記在瀏覽器（IndexedDB），依帳號分開；換電腦、換瀏覽器或清除資料後會再看到一次
 * - 關閉（✕）、略過、最後一頁的「開始使用」都算看過，不再自動跳出
 *
 * AGUI-1246 起只剩新架構的引導：
 * - 「看過了」記在 `mailNotifyGuideV2Seen:`，與舊版引導分開，看過舊版的人第一次進新版頁面仍會跳一次。
 *   ⛔ 字串不可改：改了等於清掉所有人「看過了」的紀錄，每個人都會再被跳一次
 * - 手機版也自動跳出（可以在對話裡建立規則，手機做得到）
 */

export const SEEN_KEY_PREFIX = 'mailNotifyGuideV2Seen:';

/** 目前登入的帳號（useAuth 存在 IndexedDB 的 mygenie_auth）；讀不到就回 null，不自動跳出 */
async function currentUserKey() {
  try {
    const raw = await appStorage.getItem('mygenie_auth');
    return raw ? JSON.parse(raw)?.user?.username || null : null;
  } catch {
    return null;
  }
}

/**
 * @returns {{ open: boolean, show: () => void, close: () => void }}
 */
export function useMailNotifyGuide() {
  const [open, setOpen] = useState(false);

  // 頁面每次點選單都會重新掛載（AGUI-1192），所以每次都要確認；看過就不跳
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = await currentUserKey();
      if (!user) return;
      let seen = null;
      try {
        seen = await appStorage.getItem(SEEN_KEY_PREFIX + user);
      } catch {
        // 讀不到儲存就當沒看過：多看一次引導，比該看卻沒看到好
      }
      if (!cancelled && !seen) setOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const show = useCallback(() => setOpen(true), []);

  const close = useCallback(() => {
    setOpen(false);
    void (async () => {
      const user = await currentUserKey();
      if (!user) return;
      try {
        await appStorage.setItem(SEEN_KEY_PREFIX + user, new Date().toISOString());
      } catch {
        // 寫不進去只是下次會再跳一次
      }
    })();
  }, []);

  return { open, show, close };
}

/**
 * 多頁引導對話框。上一頁／下一頁切換，最後一頁的主按鈕是「開始使用」；鍵盤 ← → 換頁、Esc 關閉，
 * Tab 只在對話框內循環，關閉後焦點回到打開前的位置。點遮罩不關閉，避免第一次進來誤觸就錯過。
 * AGUI-1257：點遮罩或對話框內的空白處時焦點不能掉到 body，否則鍵盤事件收不到——
 * 遮罩擋掉 mousedown 的預設失焦並把焦點拉回對話框，對話框本身也可聚焦（tabIndex=-1）。
 *
 * @param {{ open: boolean, onClose: () => void, pages?: typeof MAIL_NOTIFY_GUIDE_PAGES }} props
 */
export default function MailNotifyGuide({ open, onClose, pages = MAIL_NOTIFY_GUIDE_PAGES }) {
  const [index, setIndex] = useState(0);
  const dialogRef = useRef(null);
  const primaryRef = useRef(null);

  // 每次打開都從第一頁開始
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    primaryRef.current?.focus();
    return () => {
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  if (!open || pages.length === 0) return null;

  const page = pages[Math.min(index, pages.length - 1)];
  const first = index === 0;
  const last = index >= pages.length - 1;
  const next = () => setIndex((i) => Math.min(i + 1, pages.length - 1));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowRight' && !last) {
      next();
    } else if (e.key === 'ArrowLeft' && !first) {
      prev();
    } else if (e.key === 'Tab') {
      const focusables = dialogRef.current?.querySelectorAll('button:not([disabled])');
      if (!focusables || focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  };

  return createPortal(
    <div
      className="mail-notify-guide__overlay"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        if (!dialogRef.current?.contains(document.activeElement)) dialogRef.current?.focus();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="mail-notify-guide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mail-notify-guide-title"
        aria-describedby="mail-notify-guide-body"
        onKeyDown={onKeyDown}
      >
        <button type="button" className="mail-notify-guide__close" aria-label="關閉使用說明" title="關閉"
          onClick={onClose}>
          ✕
        </button>

        {/* 固定比例的媒體區：換頁時對話框高度不跳動 */}
        <div className={`mail-notify-guide__media${page.media?.mobileSrc ? ' mail-notify-guide__media--portrait' : ''}`}>
          {page.media ? (
            <picture>
              {page.media.mobileSrc && (
                <source media="(max-width: 640px)" srcSet={`${process.env.PUBLIC_URL}/${page.media.mobileSrc}`} />
              )}
              <img src={`${process.env.PUBLIC_URL}/${page.media.src}`} alt={page.media.alt || ''} />
            </picture>
          ) : (
            <div className="mail-notify-guide__placeholder" aria-hidden="true">示意圖準備中</div>
          )}
        </div>

        {/* 10-20 使用者裁決：圖上已有標題與說明，畫面不再重複顯示步驟、標題、內文，讓圖放大；
            文字仍保留給螢幕閱讀器（對話框以標題命名、以內文描述） */}
        <div className="mail-notify-guide__text">
          <div className="mail-notify-guide__step">步驟 {index + 1} / {pages.length}</div>
          <h2 id="mail-notify-guide-title" className="mail-notify-guide__title">{page.title}</h2>
          <p id="mail-notify-guide-body" className="mail-notify-guide__body">{page.body}</p>
        </div>

        <div className="mail-notify-guide__dots" aria-hidden="true">
          {pages.map((p, i) => (
            <span key={p.id} className={`mail-notify-guide__dot${i === index ? ' mail-notify-guide__dot--active' : ''}`} />
          ))}
        </div>

        <div className="mail-notify-guide__footer">
          {/* 最後一頁不放「略過」：主按鈕「開始使用」本身就是結束 */}
          {last ? <span /> : (
            <button type="button" className="mail-notify-guide__btn mail-notify-guide__btn--text" onClick={onClose}>
              略過
            </button>
          )}
          <div className="mail-notify-guide__nav">
            {!first && (
              <button type="button" className="mail-notify-guide__btn" onClick={prev}>
                上一頁
              </button>
            )}
            <button ref={primaryRef} type="button" className="mail-notify-guide__btn mail-notify-guide__btn--primary"
              onClick={last ? onClose : next}>
              {last ? '開始使用' : '下一頁'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
